import { useState, useEffect } from 'react';
import { RATES, PAYMENT, DELIVERY, BUSINESS } from '../data/pricing';
import { formatINR } from '../lib/quote';

// FAQ block plus FAQPage structured data.
//
// Two reasons this earns its place. Google renders FAQ rich results directly
// under the listing, which takes up more of the page than a competitor's plain
// link. And the questions are the actual long-tail phrases people type — "how
// many square feet does a 1.5 ton ac cool", "is installation included" — so
// the page starts matching queries the title never would.
//
// Questions are generated from real data. Nothing here is invented; if a fact
// isn't in the catalogue, the question doesn't render.

export function buildFaqs(model, price) {
  const q = [];

  q.push({
    q: `What is the price of the ${model.title}?`,
    a: `${formatINR(price)} including GST, against an MRP of ${formatINR((model.variants.withoutKit || model.variants.withKit).mrp)}. Installation is optional and charged separately at ${formatINR(RATES.installSplit.price)} before GST.`,
  });

  if (model.coverageSqft) {
    q.push({
      q: `What room size does a ${model.soldAs} ton AC cool?`,
      a: `${model.coverageSqft}. That is a guide, not a calculation — a top-floor room with an exposed roof or west-facing sun needs more capacity than the same floor area on a middle floor.`,
    });
  }

  q.push({
    q: `Is installation included in the price?`,
    a: `No. Installation is optional, so you can buy the unit on its own if you have your own fitter. If you want us to install, it is ${formatINR(RATES.installSplit.price)} plus ${formatINR(RATES.nitrogenTest.price)} for the mandatory nitrogen pressure test, plus a stand and any piping, all before GST.`,
  });

  q.push({
    q: `What is the warranty on this Daikin AC?`,
    a: model.inverter
      ? `1 year comprehensive on the unit, 10 years on the compressor and 5 years on the PCB, as per Daikin's terms from the date of invoice.`
      : `1 year comprehensive on the unit and 5 years on the compressor, as per Daikin's terms from the date of invoice.`,
  });

    if (model.variants.withKit && model.variants.withoutKit) {
    q.push({
      q: `Does this AC come with copper piping?`,
      a: `No, this unit is sold without the piping kit, which suits a replacement where your existing copper piping is sound. If you need new piping, we supply the original Daikin copper piping kit at a discounted price on request. Ask us on WhatsApp, or when we call to confirm your order.`,
    });
  } else if (model.variants.withKit) {
    q.push({
      q: `Does this AC come with copper piping?`,
      a: `Yes. This model ships with Daikin's 3 metre copper piping kit in the box.`,
    });
  }

  q.push({
    q: `What does ISEER ${model.iseer} mean for my electricity bill?`,
    a: `ISEER measures cooling delivered per unit of electricity across a season — higher is cheaper to run. At BEE's standard 1,600 hours a year this unit draws about ${model.annualKwh} kWh, roughly ${formatINR(Math.round(model.annualKwh * 5.2))} on a Delhi tariff. Noida and Ghaziabad rates are higher.`,
  });

  q.push({
    q: `Do you deliver and install in Noida and Gurgaon?`,
    a: `Yes. We cover ${BUSINESS.serviceAreas.join(', ')}. ${DELIVERY.footnote}`,
  });

  q.push({
    q: `How do I pay?`,
    a: `${PAYMENT.equipment} for the equipment. Installation is ${PAYMENT.installation.toLowerCase()}. GST invoice provided against GSTIN ${BUSINESS.gstin}.`,
  });

  return q;
}

export default function ProductFaq({ model, price }) {
  const faqs = buildFaqs(model, price);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, price]);

  return (
    <section className="mt-10 max-w-3xl">
      <h2 className="text-lg font-semibold text-gray-900">Frequently asked questions</h2>
      <div className="mt-3">
        {faqs.map((f, i) => (
          <div key={f.q} className="border-b border-gray-200">
            <button
              type="button"
              onClick={() => setOpen(open === i ? null : i)}
              aria-expanded={open === i}
              className="flex w-full items-center justify-between gap-3 py-3.5 text-left text-sm font-medium text-gray-900"
            >
              {f.q}
              <svg viewBox="0 0 20 20" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {open === i && <p className="pb-4 text-sm leading-relaxed text-gray-600">{f.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}